import { computed } from 'vue'
import { usePermissions } from '@/composables/usePermissions'
import { useDeveloperPermissions } from '@/composables/useDeveloperPermissions'

/**
 * Single source of truth for the admin navigation tree.
 *
 * The sidebar renders it as collapsible groups and AdminPanel uses it to pick a
 * landing route; both read the same definition so a screen can never appear in one
 * place and be missing from the other. Route paths here must match the children of
 * /admin in the router.
 */
export function useAdminNav() {
  const { hasPermission } = usePermissions()
  const { canAccessDeveloperTools } = useDeveloperPermissions()

  const canSeeDashboard = computed(() => hasPermission('Supplier.SuperAdmin.Dashboard'))
  const canSeeTenants = computed(() => hasPermission('Supplier.SuperAdmin.TenantSetup'))
  const canSeeUsers = computed(() => hasPermission('Supplier.SuperAdmin.UserManagement'))
  const canSeeUserMessages = computed(() => hasPermission('Supplier.SuperAdmin.UserMessages'))
  const canSeeAccessManagement = computed(() => hasPermission('Supplier.SuperAdmin.ManageAccess'))
  const canSeeDeveloper = computed(() => canAccessDeveloperTools())
  const canSeeContracts = computed(() => hasPermission('Supplier.SuperAdmin.Contracts'))
  const canSeeLocalization = computed(() => hasPermission('Supplier.SuperAdmin.Localization'))
  // Its own permission, not UserManagement: approving a company onto the platform is a
  // different responsibility from editing user rows, and usually a different person.
  const canSeeSupplierVerification = computed(() => hasPermission('Supplier.SuperAdmin.SupplierVerification'))
  // Not gated on features.subscription: super admins prepare plans before the feature is
  // switched on for tenants (the switch itself lives on the Subscription Plans screen).
  const canSeeSubscriptions = computed(() => hasPermission('Supplier.SuperAdmin.Subscriptions'))
  // Gated on the Stripe permission rather than Subscriptions: these screens expose
  // payment amounts and card metadata across every tenant.
  const canSeeStripePayments = computed(() => hasPermission('Supplier.SuperAdmin.Stripe'))

  /**
   * Groups in sidebar order. `visible` on an item decides that single entry;
   * a group renders when at least one of its items is visible.
   */
  const groups = computed(() => [
    {
      // The platform dashboard used to be the landing screen and mixed business
      // figures with import-log internals. It is retired: the business figures are
      // split across these analyses, and the technical counters moved to System
      // Health under Developer.
      key: 'analytics',
      labelKey: 'admin.groups.analytics',
      icon: 'ph-duotone ph-chart-line-up',
      items: [
        {
          key: 'analytics-quotations',
          path: '/admin/analytics/quotations',
          labelKey: 'analytics.quotations.title',
          icon: 'ph-duotone ph-file-text',
          visible: canSeeDashboard.value
        },
        {
          key: 'analytics-orders',
          path: '/admin/analytics/orders',
          labelKey: 'analytics.orders.title',
          icon: 'ph-duotone ph-shopping-cart',
          visible: canSeeDashboard.value
        },
        {
          key: 'analytics-users',
          path: '/admin/analytics/users',
          labelKey: 'analytics.users.title',
          icon: 'ph-duotone ph-users-three',
          visible: canSeeDashboard.value
        },
        {
          key: 'analytics-logins',
          path: '/admin/analytics/logins',
          labelKey: 'analytics.logins.title',
          icon: 'ph-duotone ph-sign-in',
          visible: canSeeDashboard.value
        },
        {
          key: 'analytics-suppliers',
          path: '/admin/analytics/suppliers',
          labelKey: 'analytics.suppliers.title',
          icon: 'ph-duotone ph-storefront',
          visible: canSeeDashboard.value
        },
        {
          key: 'analytics-buyers',
          path: '/admin/analytics/buyers',
          labelKey: 'analytics.buyers.title',
          icon: 'ph-duotone ph-buildings',
          visible: canSeeDashboard.value
        }
      ]
    },
    {
      key: 'tenants',
      labelKey: 'admin.groups.tenantsUsers',
      icon: 'ph-duotone ph-buildings',
      items: [
        {
          key: 'tenants',
          path: '/admin/tenants',
          labelKey: 'superAdmin.tenants.manage',
          icon: 'ph-duotone ph-building-office',
          visible: canSeeTenants.value
        },
        {
          key: 'users',
          path: '/admin/users',
          labelKey: 'superAdmin.users.manage',
          icon: 'ph-duotone ph-users',
          visible: canSeeUsers.value
        },
        {
          key: 'setup-wizard',
          path: '/admin/setup-wizard',
          labelKey: 'setupWizard.title',
          icon: 'ph-duotone ph-magic-wand',
          visible: canSeeTenants.value
        },
        {
          key: 'deleted-tenants',
          path: '/admin/deleted-tenants',
          labelKey: 'superAdmin.deletedTenants.title',
          icon: 'ph-duotone ph-trash',
          visible: canSeeTenants.value
        },
        {
          key: 'buyer-api-clients',
          path: '/admin/buyer-api-clients',
          labelKey: 'superAdmin.buyerApiClients.tabTitle',
          icon: 'ph-duotone ph-key',
          visible: canSeeTenants.value
        },
        {
          key: 'supplier-platform-settings',
          path: '/admin/supplier-platform-settings',
          labelKey: 'superAdmin.supplierPlatformSettings.tabTitle',
          icon: 'ph-duotone ph-plugs-connected',
          visible: canSeeTenants.value
        }
      ]
    },
    {
      key: 'operations',
      labelKey: 'admin.groups.operations',
      icon: 'ph-duotone ph-sliders',
      items: [
        {
          key: 'user-messages',
          path: '/admin/user-messages',
          labelKey: 'superAdmin.userMessages.title',
          icon: 'ph-duotone ph-megaphone',
          visible: canSeeUserMessages.value
        },
        {
          key: 'supplier-verification',
          path: '/admin/supplier-verification',
          labelKey: 'verification.title',
          icon: 'ph-duotone ph-seal-check',
          visible: canSeeSupplierVerification.value
        },
        {
          key: 'contract-templates',
          path: '/admin/contract-templates',
          labelKey: 'contracts.templates.title',
          icon: 'ph-duotone ph-file-text',
          visible: canSeeContracts.value
        },
        {
          key: 'localization',
          path: '/admin/localization',
          labelKey: 'localization.admin.title',
          icon: 'ph-duotone ph-translate',
          visible: canSeeLocalization.value
        }
      ]
    },
    {
      // Engineering diagnostics — kept out of Operations so a non-technical super
      // admin (CFO) never sees them. See useDeveloperPermissions for the gating.
      key: 'developer',
      labelKey: 'admin.groups.developer',
      icon: 'ph-duotone ph-code',
      items: [
        {
          key: 'system-health',
          path: '/admin/analytics/system-health',
          labelKey: 'analytics.systemHealth.title',
          icon: 'ph-duotone ph-pulse',
          visible: canSeeDeveloper.value
        },
        {
          key: 'import-log-inspector',
          path: '/admin/import-log-inspector',
          labelKey: 'superAdmin.importLogInspector.title',
          icon: 'ph-duotone ph-file-magnifying-glass',
          visible: canSeeDeveloper.value
        },
        {
          key: 'vesproc-callback-errors',
          path: '/admin/vesproc-callback-errors',
          labelKey: 'superAdmin.vesprocCallbackErrors.title',
          icon: 'ph-duotone ph-warning-octagon',
          visible: canSeeDeveloper.value
        },
        {
          key: 'webhooks',
          path: '/admin/webhooks',
          labelKey: 'superAdmin.webhooks.tabTitle',
          icon: 'ph-duotone ph-broadcast',
          visible: canSeeDeveloper.value
        }
      ]
    },
    {
      key: 'subscription',
      labelKey: 'admin.groups.subscriptions',
      icon: 'ph-duotone ph-credit-card',
      items: [
        {
          key: 'subscription-plans',
          path: '/admin/subscription-plans',
          labelKey: 'subscription.plans',
          icon: 'ph-duotone ph-tag',
          visible: canSeeSubscriptions.value
        },
        {
          key: 'tenant-subscriptions',
          path: '/admin/tenant-subscriptions',
          labelKey: 'subscription.tenantSubscriptions',
          icon: 'ph-duotone ph-receipt',
          visible: canSeeSubscriptions.value
        },
        {
          key: 'discount-codes',
          path: '/admin/discount-codes',
          labelKey: 'subscription.discountCodes.title',
          icon: 'ph-duotone ph-percent',
          visible: canSeeSubscriptions.value
        },
        {
          key: 'stripe-payments',
          path: '/admin/stripe-payments',
          labelKey: 'stripePayments.title',
          icon: 'ph-duotone ph-currency-circle-dollar',
          visible: canSeeStripePayments.value
        },
        {
          key: 'refund-requests',
          path: '/admin/refund-requests',
          labelKey: 'refundRequests.title',
          icon: 'ph-duotone ph-arrow-u-up-left',
          visible: canSeeStripePayments.value
        }
      ]
    },
    {
      key: 'security',
      labelKey: 'admin.groups.security',
      icon: 'ph-duotone ph-shield-check',
      items: [
        {
          key: 'access-management',
          path: '/admin/access-management',
          labelKey: 'superAdmin.accessManagement.tabTitle',
          icon: 'ph-duotone ph-shield-check',
          visible: canSeeAccessManagement.value
        }
      ]
    }
  ])

  /** Groups that have at least one visible item, with hidden items stripped out. */
  const visibleGroups = computed(() =>
    groups.value
      .map(group => ({ ...group, items: group.items.filter(item => item.visible) }))
      .filter(group => group.items.length > 0)
  )

  /** True when the user may open /admin at all. */
  const canAccessAdminPanel = computed(() => visibleGroups.value.length > 0)

  /** First screen the user is allowed to see — where /admin redirects to. */
  const defaultAdminPath = computed(() => visibleGroups.value[0]?.items[0]?.path ?? null)

  return {
    groups,
    visibleGroups,
    canAccessAdminPanel,
    defaultAdminPath,
    canSeeDashboard,
    canSeeTenants,
    canSeeUsers,
    canSeeUserMessages,
    canSeeAccessManagement,
    canSeeDeveloper,
    canSeeSubscriptions,
    canSeeStripePayments
  }
}
